import React from 'react';
import { useAuth } from '../context/AuthContext';
import './BillingModal.css';

const PLAN_LABELS = { monthly: 'Monthly', '3month': '3-Month', '6month': '6-Month' };

const fmtDate = (d) => new Date(d).toLocaleDateString('en-PH', { month: 'long', day: 'numeric', year: 'numeric' });

const Row = ({ label, value, valueClass }) => (
  <div className="bmodal-row">
    <span className="bmodal-row-label">{label}</span>
    <span className={`bmodal-row-value ${valueClass || ''}`}>{value}</span>
  </div>
);

const BillingModal = ({ onClose, onUpgrade }) => {
  const { user } = useAuth();
  const isPro = user?.tier === 'pro';

  const nextCreditReset = user?.lastCreditReset
    ? fmtDate(new Date(new Date(user.lastCreditReset).getTime() + 30 * 24 * 60 * 60 * 1000))
    : '—';

  const creditsUsed = user?.creditsUsed ?? 0;
  const creditsLimit = user?.creditsLimit ?? 15;
  const creditsRemaining = user?.creditsRemaining ?? creditsLimit;
  const creditPct = Math.max(0, Math.min(100, (creditsRemaining / creditsLimit) * 100));
  const barColor = creditsRemaining <= 3 ? '#EF4444' : isPro ? '#2563EB' : '#10B981';

  return (
    <div className="bmodal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bmodal-panel">

        {/* Header */}
        <div className="bmodal-header">
          <div className="bmodal-header-left">
            <div className="bmodal-header-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <rect x="1" y="4" width="22" height="16" rx="2"/>
                <line x1="1" y1="10" x2="23" y2="10"/>
              </svg>
            </div>
            <div>
              <h2 className="bmodal-title">Billing</h2>
              <p className="bmodal-subtitle">Your current plan and credit usage</p>
            </div>
          </div>
          <button className="bmodal-close" onClick={onClose}>✕</button>
        </div>

        <div className="bmodal-body">

          {/* Plan card */}
          <div className={`bmodal-plan-card ${isPro ? 'bmodal-plan-pro' : ''}`}>
            <div className="bmodal-plan-card-left">
              <div className="bmodal-plan-name">
                {isPro ? `Pro · ${PLAN_LABELS[user?.subscriptionPlan] || 'Monthly'}` : 'Starter'}
              </div>
              <div className="bmodal-plan-sub">
                {isPro ? '300 AI credits / month' : '15 AI credits / month · Free forever'}
              </div>
            </div>
            <span className={`bmodal-plan-badge ${isPro ? 'bmodal-plan-badge-pro' : 'bmodal-plan-badge-free'}`}>
              {isPro ? 'Active' : 'Free'}
            </span>
          </div>

          {/* Subscription details */}
          <div className="bmodal-section">
            <div className="bmodal-section-label">Subscription</div>
            <div className="bmodal-rows">
              <Row label="Plan" value={isPro ? `Pro · ${PLAN_LABELS[user?.subscriptionPlan] || 'Monthly'}` : 'Starter (Free)'} valueClass={isPro ? 'val-pro' : ''} />
              {isPro && user?.subscriptionEndsAt && (
                <Row label="Expires on" value={fmtDate(user.subscriptionEndsAt)} />
              )}
              {!isPro && (
                <Row label="Cost" value="Free forever" />
              )}
            </div>
          </div>

          {/* Credits section */}
          <div className="bmodal-section">
            <div className="bmodal-section-label">Credits</div>
            <div className="bmodal-credit-bar-wrap">
              <div className="bmodal-credit-bar-track">
                <div className="bmodal-credit-bar-fill" style={{ width: `${creditPct}%`, background: barColor }} />
              </div>
              <div className="bmodal-credit-bar-labels">
                <span>{creditsRemaining} remaining</span>
                <span>{creditsUsed} used of {creditsLimit}</span>
              </div>
            </div>
            <div className="bmodal-rows" style={{ marginTop: 10 }}>
              <Row label="Monthly limit" value={`${creditsLimit} credits`} />
              <Row label="Credits used" value={`${creditsUsed} credits`} />
              <Row label="Credits remaining" value={`${creditsRemaining} credits`} valueClass={creditsRemaining <= 3 ? 'val-low' : ''} />
              <Row label="Resets on" value={nextCreditReset} />
            </div>
          </div>

          {/* Action */}
          <div className="bmodal-actions">
            {isPro ? (
              <button className="bmodal-btn-manage" onClick={onUpgrade}>
                Manage Plan
              </button>
            ) : (
              <button className="bmodal-btn-upgrade" onClick={onUpgrade}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                </svg>
                Upgrade to Pro — ₱199/mo
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default BillingModal;
